import React, { useContext, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip, Box,
  Typography, Alert, TextField, Divider,
} from "@mui/material";
import { MdOutlineFlightTakeoff } from "react-icons/md";
import { ApplicationContext } from "shared";
import {
  parseAtc, CONTROLLED_ATC, findControlledMedicines, CONTROLLED_META,
} from "common/controlledSubstances";

/**
 * Inline "Medication Travel" flag for the Medicines list.
 *
 * - ControlledChip: the small pill shown next to a controlled medicine.
 * - MedTravelDialog: opened from any chip — guidance (never a legality verdict)
 *   plus a printable travel letter covering all of the member's controlled meds.
 *
 * Gated by REACT_APP_MEDTRAVEL_ENABLED (defaults OFF).
 */
export const MED_TRAVEL_ENABLED =
  process.env.REACT_APP_MEDTRAVEL_ENABLED === "true";

const AUTHORITY_URL =
  process.env.REACT_APP_MEDTRAVEL_AUTHORITY_URL ||
  "https://www.incb.org/incb/en/travellers/country-regulations.html";

// True when a medicine description ("A07DA01 - Diphenoxylate") is controlled.
export const isControlledDescription = (description) => {
  const atc = parseAtc(description);
  return !!(atc && CONTROLLED_ATC[atc]);
};

const listLabel = (list) =>
  list.includes("narcotic") && list.includes("psychotropic")
    ? "Narcotic & psychotropic"
    : list.includes("narcotic")
    ? "Narcotic (1961 Convention)"
    : "Psychotropic (1971 Convention)";

export const ControlledChip = ({ onClick }) => (
  <Chip
    size="small"
    color="warning"
    icon={<MdOutlineFlightTakeoff size={15} />}
    label="Travel info"
    onClick={(e) => {
      e.stopPropagation();
      onClick && onClick();
    }}
    sx={{ ml: 1, height: 22, cursor: "pointer", fontSize: 11 }}
  />
);

export const MedTravelDialog = ({ open, onClose, medicines }) => {
  const ctx = useContext(ApplicationContext);
  const userData = ctx?.userData || {};
  const [passportRef, setPassportRef] = useState("");
  const [details, setDetails] = useState({}); // atc -> {dose, quantity, prescriber}

  const flagged = useMemo(
    () => findControlledMedicines(medicines || []),
    [medicines]
  );

  const fullName = [userData.firstName, userData.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const setDetail = (atc, key, value) =>
    setDetails((d) => ({ ...d, [atc]: { ...(d[atc] || {}), [key]: value } }));

  const openLetter = () => {
    const win = window.open("", "_blank", "width=820,height=1000");
    if (!win) return;
    const esc = (s) =>
      String(s || "").replace(/[<>&]/g, (c) =>
        ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c])
      );
    const rows = flagged
      .map((f) => {
        const d = details[f.atc] || {};
        return `<tr>
          <td>${esc(f.name)} <span class="atc">(${esc(f.atc)})</span></td>
          <td>${esc(d.dose) || "&nbsp;"}</td>
          <td>${esc(d.quantity) || "&nbsp;"}</td>
          <td>${esc(d.prescriber) || "&nbsp;"}</td>
        </tr>`;
      })
      .join("");
    const today = new Date().toLocaleDateString("en-GB", {
      year: "numeric", month: "long", day: "numeric",
    });
    const logoUrl = `${window.location.origin}/images/WMC_logo_slogan_black.png`;
    win.document.write(`<!doctype html><html><head><meta charset="utf-8">
      <title>Medication travel letter</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;max-width:720px;margin:40px auto;line-height:1.5;padding:0 24px;}
        h1{font-size:20px;margin:0 0 4px;}
        .sub{color:#555;font-size:13px;margin-bottom:24px;}
        .meta p{margin:2px 0;font-size:14px;}
        table{width:100%;border-collapse:collapse;margin:18px 0;font-size:13px;}
        th,td{border:1px solid #cfd6e4;padding:8px 10px;text-align:left;vertical-align:top;}
        th{background:#f1f5fb;}
        .atc{color:#64748b;font-size:11px;}
        .note{font-size:11px;color:#64748b;border-top:1px solid #e2e8f0;margin-top:28px;padding-top:12px;}
        .issuer{margin-top:28px;font-size:13px;}
        @media print{button{display:none;}}
        button{margin-top:24px;padding:10px 18px;font-size:14px;cursor:pointer;}
      </style></head><body>
      <img src="${logoUrl}" alt="World Medical Card" style="height:52px;margin-bottom:18px;" />
      <h1>Medication travel letter</h1>
      <div class="sub">Issued ${today}</div>
      <div class="meta">
        <p><strong>Traveller:</strong> ${esc(fullName) || "_____________________"}</p>
        <p><strong>Date of birth:</strong> ${esc(userData.birthDate) || "_____________________"}</p>
        <p><strong>Passport / ID reference:</strong> ${esc(passportRef) || "_____________________"}</p>
      </div>
      <p style="font-size:14px;">This traveller carries the following prescribed medicine(s) for personal
      medical use. The medicine(s) below are internationally controlled under the United Nations drug-control
      conventions. This letter is provided to support the traveller at border control. It does not constitute
      authorisation to import or carry these medicines into any country — the traveller remains responsible for
      complying with the regulations of each country visited.</p>
      <table>
        <thead><tr><th>Medicine (INN &amp; ATC)</th><th>Dose</th><th>Quantity carried</th><th>Prescriber</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="issuer">
        <p>Issued by <strong>World Medical Card</strong></p>
        <p>This letter reflects information held in the member's World Medical Card profile.</p>
      </div>
      <div class="note">World Medical Card does not determine or warrant the legal status of any medicine in any
      country. Before travelling, confirm the applicable rules with the destination country's embassy or the
      relevant national authority.</div>
      <button onclick="window.print()">Print / Save as PDF</button>
      </body></html>`);
    win.document.close();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Travelling with your medicines</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {flagged.length > 0
            ? `${flagged.length} of your medicine${flagged.length > 1 ? "s are" : " is"} internationally controlled. This does not mean ${flagged.length > 1 ? "they are" : "it is"} banned — it means you should prepare paperwork and check the rules for each country you visit.`
            : "None of your medicines are on the international controlled-substance lists. Rules can still vary by country, so check your destination before you travel."}
        </Alert>

        {flagged.map((f) => (
          <Box key={f.atc} sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 0.5 }}>
              <Typography fontWeight={600}>{f.name}</Typography>
              <Chip size="small" variant="outlined" label={f.atc} />
              <Chip
                size="small"
                color={f.list.includes("narcotic") ? "error" : "warning"}
                label={listLabel(f.list)}
              />
              <Chip size="small" variant="outlined" label={`Schedule ${f.schedule.join("/")}`} />
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <TextField size="small" label="Dose" sx={{ flex: "1 1 120px" }}
                value={details[f.atc]?.dose || ""}
                onChange={(e) => setDetail(f.atc, "dose", e.target.value)} />
              <TextField size="small" label="Quantity" sx={{ flex: "1 1 120px" }}
                value={details[f.atc]?.quantity || ""}
                onChange={(e) => setDetail(f.atc, "quantity", e.target.value)} />
              <TextField size="small" label="Prescriber" sx={{ flex: "1 1 120px" }}
                value={details[f.atc]?.prescriber || ""}
                onChange={(e) => setDetail(f.atc, "prescriber", e.target.value)} />
            </Box>
          </Box>
        ))}

        {flagged.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography fontWeight={700} sx={{ mb: 1 }}>Before you travel</Typography>
            <Typography component="div" color="text.secondary" variant="body2">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Check the rules for every country on your trip, including stopovers.</li>
                <li>Carry your original prescription and keep medicines in their original, labelled packaging.</li>
                <li>Bring a doctor's letter and this travel letter.</li>
                <li>Carry only the quantity you need; some countries set limits.</li>
                <li>Declare controlled medicines at customs if asked.</li>
              </ul>
            </Typography>
            <Button href={AUTHORITY_URL} target="_blank" rel="noopener noreferrer" sx={{ mt: 1, px: 0 }}>
              Check destination rules (official source)
            </Button>
            <Box sx={{ mt: 2 }}>
              <TextField size="small" fullWidth label="Passport / ID reference (optional)"
                value={passportRef} onChange={(e) => setPassportRef(e.target.value)} />
            </Box>
          </>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          {CONTROLLED_META.note}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {flagged.length > 0 && (
          <Button variant="contained" onClick={openLetter}>Open printable letter</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
